import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { Box } from '@mui/system';
import CancelIcon from '@mui/icons-material/Close';
import EnhancedTextarea from 'components/EnhancedTextarea';
import RowSpacer from 'components/RowSpacer';
import Card from 'components/Card';
import CardActions from 'components/Card/Actions';
import CardText from 'components/Card/Text';
import Dialog from 'components/Dialog';
import LoadBlur from 'components/LoadBlur';
import auth from 'lib/auth';
import store from 'lib/store';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DELETE_ACCOUNT_OTP, DELETE_ACCOUNT } from '../../../schema/apolloMutations/profileMutations';
import { GET_PERSON_INFO } from '../../../schema/apolloQueries/profileQueries';
import OTP from '../AboutMe/OneTimePwd';

const styles = {
  dialogBody: { textAlign: 'center' },
  cardHeadrTitle: { color: '#0076BB', padding: '0 22px 16px' },
  cardText: { color: '#000', paddingBottom: 24 },
  cardLayout: {
    borderRadius: 15,
    backgroundColor: '#FFF',
    textAlign: 'center',
    padding: ' 15px',
    maxHeight: '80vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  left: { width: '5%' },
  cardActions: { display: 'inline-flex', paddingBottom: 24 },
  button: { borderRadius: 15, backgroundColor: '#0076BB', color: '#FFF' },
  buttonRed: { borderRadius: 15, backgroundColor: '#EF493E', color: '#FFF' },
  descriptionNew: {
    fontFamily: 'jost',
    fontSize: '16px',
    lineHeight: '20px',
    textAlign: 'center',
    marginTop: '2%',
    marginBottom: '2%',
    color: '#656565',
  },
  dint: {
    fontFamily: 'jost',
    fontWeight: 400,
    fontSize: '16px',
    textAlign: 'center',
    marginBottom: '6%',
    marginTop: '6%',
    color: '#424141',
  },
  resend: {
    color: 'blue',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: '600',
  },
  listItem: {
    marginBottom: '10px',
    fontFamily: 'jost',
    fontSize: '16px',
    fontWeight: '400',
  },
  errortext: {
    fontFamily: 'NewOrderBold',
    fontSize: '18px',
    textAlign: 'center',
    marginBottom: '5%',
    color: '#EF493E',
    marginTop: '6%',
  },
};

const reasonOptions = [
  'Wasn’t selected for events I applied to',
  'Didn’t find products that I was interested in',
  'No longer interested in product trials',
  'Event experience issues',
  'Other',
];

const DeleteAccount = ({ isDelete, setIsDelete }) => {
  const [viewer, setViewer] = useState({});
  const { data } = useQuery(GET_PERSON_INFO);
  const [deleteAccountOtp] = useMutation(DELETE_ACCOUNT_OTP);
  const [deleteAccount] = useMutation(DELETE_ACCOUNT);

  const [errorText, setErrorText] = useState(null);
  const [isInitalPage, setIsInitalPage] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [timer, setTimer] = useState();
  const [otp, setOtp] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [comment, setComment] = useState('');
  const [loader, setLoader] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const email = viewer?.personInfo?.email;

  useEffect(() => {
    setViewer(data?.viewer);
  }, [data]);

  useEffect(() => {
    let interval;
    if (isActive && timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    } else if (timer === 0) {
      setIsActive(false);
    }
    return () => clearInterval(interval);
  }, [isActive, timer]);

  const remainingMinutes = Math.floor((timer || 0) / 60);
  const remainingSeconds = (timer || 0) % 60 < 10 ? `0${(timer || 0) % 60}` : (timer || 0) % 60;

  const handleReasonChange = (e) => {
    setSelectedReason(e.target.value);
    if (e.target.value !== 'Other') {
      setComment('');
      setErrorText(null);
    }
  };

  const onChange = e => {
    const { value } = e.target;
    if (value.length >= 255) {
      setErrorText('Maximum 255 characters allowed.');
      return;
    }
    setErrorText(null);
    setComment(value);
  };
  const onSendotp = () => {
    deleteAccountOtp()
      .then(res => {
        const otpData = JSON.parse(res.data.deleteAccountOtp);
        setIsActive(true);
        setTimer(otpData.interval * 60);
      })
      .catch(e => setErrorText(e.message));
  };

  const handleProceed = () => {
    if (!isInitalPage && otp.length === 4) {
      setLoader(true);
      let payload;
      if (selectedReason === 'Other') {
        payload = { reason: `${selectedReason}:${comment}`, otp, channel: 'web' };
      } else {
        payload = { reason: selectedReason, otp, channel: 'web' };
      }
      deleteAccount({ variables: payload })
        .then(async () => {
          setLoader(false);
          setShowSuccessModal(true); // Show success modal after submit
          setIsDelete(false); // Close the delete account modal
        })
        .catch(() => {
          setErrorText('Invalid OTP. Please try again.');
          setLoader(false);
        });
    } else {
      onSendotp();
      setIsInitalPage(false);
    }
  };

  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    setErrorText(null);
  };
  return (
    <>
      <Dialog
        onRequestClose={() => setIsDelete(false)}
        open={isDelete}
        bodyStyle={
          styles.dialogBody
        }
      >
        <Card
          style={
            styles.cardLayout

          }
          rounded
        >
          <LoadBlur loading={loader}>
            <div style={styles.left}>
              <IconButton onClick={() => setIsDelete(false)} size='large'>
                <CancelIcon style={{ color: '#0076BB' }} />
              </IconButton>
            </div>
            <Typography style={styles.cardHeadrTitle} variant='h6'>
              Delete Account
            </Typography>
            {isInitalPage ? (
              <CardText style={styles.cardText} noTopPadding>
                <div style={styles.descriptionNew}>
                  We are sorry to see you go. Deleting your account is permanent. Once your account is deleted, you will no longer be able to access any of the content that you have shared with Ripple Street
                </div>
                <List sx={{ textAlign: 'left', marginLeft: '20px' }}>
                  {[
                    'User profile and preferences',
                    'Reviews that you have created',
                    'UGC posts including pictures and video',
                    'Event history for events in which you have participated',
                    'Support tickets',
                  ].map((item) => (
                    <ListItem sx={styles.listItem} key={item}>
                      <span style={{ fontSize: '20px', verticalAlign: 'middle', marginRight: 8 }}>•</span>
                      {item}
                    </ListItem>
                  ))}
                </List>
              </CardText>
            ) : (
              <CardText style={styles.cardText} noTopPadding>
                <div style={styles.errortext}>{errorText}</div>
                <div style={styles.descriptionNew}>
                  Please let us know the reason you wish to delete your Ripple Street account?
                </div>
                <FormControl
                  fullWidth
                  variant='standard'
                  sx={{
                    marginBottom: 2,
                    '& .MuiInputLabel-root': { color: '#000' },
                    '& .MuiSelect-root, & .MuiInputBase-root:before': { borderColor: '#000' },
                    '& .MuiInput-underline:before': { borderBottomColor: '#000' },
                    '& .MuiInput-underline:hover:before': { borderBottomColor: '#000' },
                    '& .MuiInput-underline:after': { borderBottomColor: '#000' },
                  }}
                >
                  <InputLabel id='delete-reason-label' sx={{ color: '#000' }}>
                    Select Reason
                  </InputLabel>
                  <Select
                    labelId='delete-reason-label'
                    value={selectedReason}
                    onChange={handleReasonChange}
                    label='Select Reason'
                    sx={{
                      color: '#000',
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#fff', // menu background white
                          color: '#000', // menu items black
                        },
                      },
                    }}
                    IconComponent={(props) => <ArrowDropDownIcon {...props} style={{ color: '#000' }} />}
                  >
                    {reasonOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedReason === 'Other' && (
                  <EnhancedTextarea
                    borderColor='#dfdfdf'
                    color='#000'
                    autoFocus={false}
                    name='body'
                    onChange={onChange}
                    placeholder='Type here...'
                    value={comment}
                    maxLength={255}
                  />
                )}
                <div style={styles.descriptionNew}>
                  Authenticate your identity
                  <span style={{ color: '#EF493E', marginLeft: 6 }}>*</span>
                </div>
                <div>
                  A verification code has been sent to the email address you registered with.
                  Please enter it below to secure your account.
                  <br />({email})
                </div>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    alignItems: 'center',
                    marginTop: '2%',
                  }}
                >
                  <OTP
                    separator={<span>-</span>}
                    value={otp}
                    onChange={handleOtpChange}
                    length={4}
                  />
                </Box>
                <div style={styles.dint}>
                  Didn’t receive?{' '}
                  <span
                    style={{
                      ...styles.resend,
                      pointerEvents: isActive ? 'none' : 'inherit',
                      opacity: isActive ? 0.3 : 1,
                    }}
                    onClick={onSendotp}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') onSendotp();
                    }}
                    tabIndex={0}
                    role='button'
                  >
                    Resend
                  </span>
                  {isActive && (
                    <span
                      style={{
                        marginLeft: '2%',
                        fontFamily: 'jost',
                        fontSize: '16px',
                        fontWeight: 'bold',
                      }}
                    >
                      {' '}
                      {`${remainingMinutes}:${remainingSeconds}`}
                    </span>
                  )}
                </div>
              </CardText>
            )}
            <CardActions noTopPadding style={styles.cardActions}>
              <Button
                style={{
                  ...styles.buttonRed,
                  width: '120px',
                  opacity: isInitalPage ? 0.5 : 1,
                }}
                variant='contained'
                onClick={() => setIsInitalPage(!isInitalPage)}
                disabled={isInitalPage}
              >
                Previous
              </Button>
              <RowSpacer />
              <Button
                disabled={!isInitalPage && otp.length < 4}
                style={{
                  ...styles.button,
                  width: '120px',
                  opacity: !isInitalPage && otp.length < 4 ? 0.5 : 1,
                }}
                variant='contained'
                onClick={handleProceed}
              >
                {isInitalPage ? 'Next' : 'Submit'}
              </Button>
            </CardActions>
          </LoadBlur>
        </Card>
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onRequestClose={() => {
          setShowSuccessModal(false);
          setIsDelete(false);
        }}
        bodyStyle={styles.dialogBody}
      >
        <Card style={styles.cardLayout} rounded>
          <CardText style={styles.cardText} noTopPadding>
            <div style={styles.descriptionNew}>
              Your request to delete your account has been processed.
            </div>
          </CardText>
          <Button
            style={{
              ...styles.button,
              width: '120px',
              alignSelf: 'center',
            }}
            variant='contained'
            onClick={async () => {
              await store.save('skipUntilNextLogin', false);
              await auth.logout();
              setShowSuccessModal(false);
              setIsDelete(false);
            }}
          >
            Okay
          </Button>
        </Card>
      </Dialog>
    </>
  );
};

DeleteAccount.propTypes = {
  isDelete: PropTypes.object.isRequired,
  setIsDelete: PropTypes.func.isRequired,
};

export default DeleteAccount;
